import React, { useState, useEffect } from 'react';
import { clientAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users, 
  Plus, 
  Loader2, 
  Pencil, 
  Trash2, 
  Eye,
  Calendar,
  Phone,
  Mail,
  UserCircle,
  PartyPopper,
  Briefcase,
  Heart,
  IndianRupee,
  Search,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';

const EVENT_TYPES = ['Wedding', 'Leisure', 'Corporate', 'Conference', 'Birthday', 'Anniversary', 'Other'];

const EVENT_ICONS = {
  'Wedding': Heart,
  'Corporate': Briefcase,
  'Conference': Briefcase,
  'Leisure': PartyPopper,
  'Birthday': PartyPopper,
  'Anniversary': Heart,
  'Other': UserCircle
};

const EVENT_COLORS = {
  'Wedding': 'bg-pink-100 text-pink-800',
  'Corporate': 'bg-blue-100 text-blue-800',
  'Conference': 'bg-indigo-100 text-indigo-800',
  'Leisure': 'bg-green-100 text-green-800',
  'Birthday': 'bg-yellow-100 text-yellow-800',
  'Anniversary': 'bg-red-100 text-red-800',
  'Other': 'bg-gray-100 text-gray-800'
};

export const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEventType, setFilterEventType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  
  // Selection state for checkboxes
  const [selectedClients, setSelectedClients] = useState([]);
  const [downloading, setDownloading] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPOs, setClientPOs] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    check_in: '',
    check_out: '',
    number_of_pax: '',
    event_type: 'Wedding',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    notes: ''
  });

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterEventType !== 'all') params.event_type = filterEventType;
      
      const response = await clientAPI.getAll(params);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [filterStatus, filterEventType]);

  const handleOpenCreate = () => {
    setEditingClient(null);
    setFormData({
      client_name: '',
      check_in: '',
      check_out: '',
      number_of_pax: '',
      event_type: 'Wedding',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name || '',
      check_in: client.check_in || '',
      check_out: client.check_out || '',
      number_of_pax: client.number_of_pax?.toString() || '',
      event_type: client.event_type || 'Wedding',
      contact_person: client.contact_person || '',
      contact_phone: client.contact_phone || '',
      contact_email: client.contact_email || '',
      notes: client.notes || ''
    });
    setDialogOpen(true);
  };

  const handleViewClient = async (client) => {
    try {
      const response = await clientAPI.getPurchaseOrders(client.id);
      setSelectedClient(client);
      setClientPOs(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch client details');
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_name || !formData.check_in || !formData.check_out || !formData.number_of_pax) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        number_of_pax: parseInt(formData.number_of_pax)
      };

      if (editingClient) {
        await clientAPI.update(editingClient.id, data);
        toast.success('Client updated successfully');
      } else {
        await clientAPI.create(data);
        toast.success('Client created successfully');
      }
      
      setDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.client_name}"?`)) return;
    
    try {
      await clientAPI.delete(client.id);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const filteredClients = clients.filter(c => {
    const matchesSearch = 
      c.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.contact_person?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.contact_phone?.includes(searchQuery);
    return matchesSearch;
  });

  const getEventIcon = (eventType) => {
    const Icon = EVENT_ICONS[eventType] || UserCircle;
    return <Icon className="h-4 w-4" />;
  };

  // Selection handlers
  const handleSelectClient = (clientId, checked) => {
    if (checked) {
      setSelectedClients([...selectedClients, clientId]);
    } else {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(filteredClients.map(c => c.id));
    } else {
      setSelectedClients([]);
    }
  };

  const isAllSelected = filteredClients.length > 0 && selectedClients.length === filteredClients.length;
  const isSomeSelected = selectedClients.length > 0 && selectedClients.length < filteredClients.length;

  // Download expense summary PDF
  const handleDownloadExpenseSummary = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    try {
      setDownloading(true);
      const response = await clientAPI.downloadExpenseSummary(selectedClients);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = selectedClients.length > 1 
        ? `Expense_Summary_Multiple_Clients.pdf` 
        : `Expense_Summary.pdf`;
      
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded expense summary for ${selectedClients.length} client(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download expense summary');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Clients & Events</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage client bookings and track event expenses</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedClients.length > 0 && (
            <Button 
              onClick={handleDownloadExpenseSummary} 
              disabled={downloading}
              variant="outline"
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              data-testid="download-expense-summary-btn"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Summary ({selectedClients.length})
            </Button>
          )}
          <Button onClick={handleOpenCreate} className="rounded-full" data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Clients</p>
                <p className="text-xl font-bold">{clients.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-100">
                <Heart className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Weddings</p>
                <p className="text-xl font-bold">{clients.filter(c => c.event_type === 'Wedding').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Corporate</p>
                <p className="text-xl font-bold">{clients.filter(c => c.event_type === 'Corporate').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <IndianRupee className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Expenses</p>
                <p className="text-xl font-bold">{formatCurrency(clients.reduce((sum, c) => sum + (c.total_expenses || 0), 0))}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, contact..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-clients"
              />
            </div>
            <Select value={filterEventType} onValueChange={setFilterEventType}>
              <SelectTrigger className="w-full md:w-[150px]" data-testid="filter-event-type">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EVENT_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[120px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {/* Selection Info Bar */}
          {selectedClients.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedClients([])}
                className="text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </Button>
            </div>
          )}
          
          {/* Custom Table with Checkboxes */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No clients found</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                        className="translate-y-[2px]"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Check In</TableHead>
                    <TableHead>Check Out</TableHead>
                    <TableHead className="text-center">Pax</TableHead>
                    <TableHead className="text-center">POs</TableHead>
                    <TableHead className="text-right">Expenses</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map(client => (
                    <TableRow 
                      key={client.id}
                      className={selectedClients.includes(client.id) ? 'bg-blue-50/50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedClients.includes(client.id)}
                          onCheckedChange={(checked) => handleSelectClient(client.id, checked)}
                          aria-label={`Select ${client.client_name}`}
                          className="translate-y-[2px]"
                          data-testid={`select-client-${client.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{client.client_name}</p>
                          {client.contact_person && (
                            <p className="text-xs text-muted-foreground">{client.contact_person}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${EVENT_COLORS[client.event_type] || EVENT_COLORS['Other']} gap-1`}>
                          {getEventIcon(client.event_type)} {client.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(client.check_in)}</TableCell>
                      <TableCell>{formatDate(client.check_out)}</TableCell>
                      <TableCell className="text-center">{client.number_of_pax}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{client.total_pos || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(client.total_expenses || 0)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleViewClient(client)} title="View">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(client)} title="Edit">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(client)} title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Update client details' : 'Enter client and event information'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="client_name">Client Name *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="e.g., John Smith Wedding"
                  data-testid="input-client-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="event_type">Event Type *</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(v) => setFormData({ ...formData, event_type: v })}
                >
                  <SelectTrigger data-testid="select-event-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="number_of_pax">Number of Pax *</Label>
                <Input
                  id="number_of_pax"
                  type="number"
                  min="1"
                  value={formData.number_of_pax}
                  onChange={(e) => setFormData({ ...formData, number_of_pax: e.target.value })}
                  placeholder="150"
                  data-testid="input-pax"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="check_in">Check In Date *</Label>
                <Input
                  id="check_in"
                  type="date"
                  value={formData.check_in}
                  onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                  data-testid="input-check-in"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="check_out">Check Out Date *</Label>
                <Input
                  id="check_out"
                  type="date"
                  value={formData.check_out}
                  onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                  data-testid="input-check-out"
                />
              </div>
              <div className="col-span-2 border-t pt-4 mt-2">
                <p className="text-sm font-medium mb-3">Contact Information</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="John Smith"
                  data-testid="input-contact-person"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="9876543210"
                  data-testid="input-contact-phone"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="input-contact-email"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special requirements..."
                  rows={2}
                  data-testid="input-notes"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-client-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingClient ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Client Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {selectedClient && getEventIcon(selectedClient.event_type)}
              {selectedClient?.client_name}
            </DialogTitle>
            <DialogDescription>
              Client details and associated purchase orders
            </DialogDescription>
          </DialogHeader>
          
          {selectedClient && clientPOs && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4 md:p-6 bg-muted rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Event Type</p>
                  <Badge className={EVENT_COLORS[selectedClient.event_type]}>
                    {selectedClient.event_type}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Check In</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(selectedClient.check_in)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Check Out</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(selectedClient.check_out)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Guests</p>
                  <p className="font-medium">{selectedClient.number_of_pax} pax</p>
                </div>
              </div>

              {/* Contact Info */}
              {(selectedClient.contact_person || selectedClient.contact_phone || selectedClient.contact_email) && (
                <div className="flex flex-wrap gap-4 text-sm">
                  {selectedClient.contact_person && (
                    <div className="flex items-center gap-1">
                      <UserCircle className="h-4 w-4 text-muted-foreground" />
                      {selectedClient.contact_person}
                    </div>
                  )}
                  {selectedClient.contact_phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      {selectedClient.contact_phone}
                    </div>
                  )}
                  {selectedClient.contact_email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      {selectedClient.contact_email}
                    </div>
                  )}
                </div>
              )}

              {/* Expense Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Estimated</p>
                    <p className="text-lg font-bold">{formatCurrency(clientPOs.total_estimated || 0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Actual</p>
                    <p className="text-lg font-bold">{formatCurrency(clientPOs.total_actual || 0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Paid</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(clientPOs.total_paid || 0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Balance</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(clientPOs.total_balance || 0)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Purchase Orders */}
              <div>
                <h4 className="font-medium mb-2">Purchase Orders ({clientPOs.purchase_orders?.length || 0})</h4>
                {clientPOs.purchase_orders?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>PO #</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientPOs.purchase_orders.map(po => (
                        <TableRow key={po.id}>
                          <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                          <TableCell>{po.vendor_name || po.custom_vendor_name || '-'}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(po.client_amount ?? (po.actual_amount || po.total_amount))}
                            {po.is_multi_client && (
                              <span className="ml-1 text-[10px] text-amber-600" title="Shared PO — amount allocated to this client via GRN">shared</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{po.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-sm py-4 text-center">No purchase orders linked to this client</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
